<template>
    <div
        ref="root"
        class="disc-showcase"
        :class="{ 'is-open': isOpen, 'is-locked': !isSettled }"
        :style="{
            '--img-jacket': `url('${jacketSrc}')`,
            '--img-water': `url('${waterSrc}')`,
            '--img-disc': `url('${discSrc}')`,
        }"
        @pointerenter="onPointerEnter"
        @pointerleave="onPointerLeave"
        @pointercancel="releaseTilt"
        @pointerdown="onPointerDown"
    >
        <div class="ripples">
            <div class="animate-[ripple-out_2.4s_ease-out_0.75s_both]" />
            <div class="animate-[ripple-out_2.4s_ease-out_0.95s_both]" />
            <div class="animate-[ripple-out_2.7s_ease-out_1.15s_both]" />
        </div>

        <div class="animate-[disc-drop_1.4s_cubic-bezier(0.22,1,0.36,1)_both]">
            <div class="animate-[disc-float_8s_ease-in-out_1.5s_infinite]">
                <div class="assembly">
                    <div class="disc-carrier">
                        <div class="disc-holder">
                            <div class="disc-face" />

                            <div class="disc-backlight">
                                <div class="disc-backlight-face" />
                            </div>

                            <div class="disc-label" />
                            <div class="disc-gloss" />
                        </div>
                    </div>

                    <div ref="record" class="record">
                        <div class="layer layer-interior" />

                        <div class="sleeve-carrier">
                            <div class="sleeve"><i /></div>
                        </div>

                        <div class="window-depth" />
                        <div class="layer layer-jacket" />
                        <div class="corona" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    // Library
    import {
        defineComponent,
        onBeforeUnmount,
        onMounted,
        ref,
    } from 'vue';
    import jacketSrc from '@/img/showcase-jacket.webp';
    import waterSrc from '@/img/showcase-water.webp';
    import discSrc from '@/img/Vinyl disc.png';
    import { whenImagesLoaded } from '@/library/preload';

    const INTRO_DURATION_MS = 2050;
    const MAX_IMAGE_WAIT_MS = 3000;
    const MAX_TILT_DEG = 13;
    const SETTLE_BACK_MS = 700;

    export default defineComponent({
        name: 'DiscHero',

        emits: ['ready'],

        setup(props, { emit }) {
            const root = ref(null);
            const record = ref(null);
            const isOpen = ref(false);
            const isSettled = ref(false);

            let isPointerOver = false;
            let isTapped = false;
            let hasSettledOpen = false;
            let introTimer = null;
            let settleTimer = null;
            let breathTimer = null;
            let breathFrame = 0;
            let breathStart = 0;
            let prefersReducedMotion = false;

            const readProperty = (name) => getComputedStyle(root.value).getPropertyValue(name).trim();

            const readMilliseconds = (name) => {
                const value = readProperty(name);

                return value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
            };

            const setTilt = (x, y) => {
                record.value.style.setProperty('--tilt-x', `${x}deg`);
                record.value.style.setProperty('--tilt-y', `${y}deg`);
            };

            const stopBreathing = () => {
                window.clearTimeout(breathTimer);
                window.cancelAnimationFrame(breathFrame);
                breathFrame = 0;

                if (record.value) {
                    record.value.classList.remove('is-breathing');
                }
            };

            const breathe = (timestamp) => {
                const period = readMilliseconds('--breath-period');
                const amplitude = parseFloat(readProperty('--breath-angle'));

                if (breathStart === 0) {
                    breathStart = timestamp;
                }

                const phase = ((timestamp - breathStart) % period) / period;
                const angle = Math.sin(phase * Math.PI * 2) * amplitude;

                record.value.style.setProperty('--tilt-y', `${angle.toFixed(2)}deg`);
                breathFrame = window.requestAnimationFrame(breathe);
            };

            const startBreathing = () => {
                if (breathFrame !== 0 || isPointerOver || prefersReducedMotion || !record.value) {
                    return;
                }

                breathStart = 0;
                record.value.classList.add('is-breathing');
                breathFrame = window.requestAnimationFrame(breathe);
            };

            const closeSleeve = () => {
                if (!isOpen.value || !hasSettledOpen || isPointerOver || isTapped) {
                    return;
                }

                isOpen.value = false;
            };

            const openSleeve = () => {
                if (isOpen.value || !isSettled.value) {
                    return;
                }

                isOpen.value = true;
                hasSettledOpen = false;

                window.clearTimeout(settleTimer);
                settleTimer = window.setTimeout(() => {
                    hasSettledOpen = true;
                    closeSleeve();
                }, readMilliseconds('--open-duration') + readMilliseconds('--hold-duration'));
            };

            const trackPointer = (event) => {
                if (!isSettled.value || !record.value || event.pointerType !== 'mouse') {
                    return;
                }

                const horizontal = event.clientX / window.innerWidth;
                const vertical = event.clientY / window.innerHeight;

                stopBreathing();
                record.value.classList.add('is-tracking');
                setTilt(
                    ((0.5 - vertical) * MAX_TILT_DEG * 2).toFixed(2),
                    ((horizontal - 0.5) * MAX_TILT_DEG * 2).toFixed(2),
                );
            };

            const releaseTilt = () => {
                if (!record.value) {
                    return;
                }

                record.value.classList.remove('is-tracking');
                setTilt(0, 0);
            };

            const onPointerEnter = (event) => {
                if (event.pointerType !== 'mouse') {
                    return;
                }

                isPointerOver = true;
                stopBreathing();
                openSleeve();
            };

            const onPointerLeave = () => {
                isPointerOver = false;
                closeSleeve();
            };

            const onWindowPointerLeave = () => {
                releaseTilt();
                breathTimer = window.setTimeout(startBreathing, SETTLE_BACK_MS);
            };

            const onPointerDown = (event) => {
                if (event.pointerType === 'mouse') {
                    return;
                }

                isTapped = !isTapped;

                if (isTapped) {
                    openSleeve();

                    return;
                }

                hasSettledOpen = true;
                closeSleeve();
            };

            onMounted(() => {
                prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

                window.addEventListener('pointermove', trackPointer, {
                    passive: true,
                });
                document.documentElement.addEventListener('pointerleave', onWindowPointerLeave);

                whenImagesLoaded([jacketSrc, waterSrc, discSrc], MAX_IMAGE_WAIT_MS).then(() => {
                    if (!root.value) {
                        return;
                    }

                    emit('ready');

                    introTimer = window.setTimeout(() => {
                        isSettled.value = true;
                        startBreathing();
                    }, INTRO_DURATION_MS);
                });
            });

            onBeforeUnmount(() => {
                window.removeEventListener('pointermove', trackPointer);
                document.documentElement.removeEventListener('pointerleave', onWindowPointerLeave);
                window.clearTimeout(introTimer);
                window.clearTimeout(settleTimer);
                stopBreathing();
            });

            return {
                root,
                record,
                isOpen,
                isSettled,
                jacketSrc,
                waterSrc,
                discSrc,
                releaseTilt,
                onPointerEnter,
                onPointerLeave,
                onPointerDown,
            };
        },
    });
</script>

<style>
    .disc-showcase {
        /* the open state spans ~2.2x this width, so it has to stay under half the viewport */
        --assembly: min(400px, 54vh, 45vw);
        --hole: 29.77%;
        --sleeve-travel: 28%;
        --disc-inset: 7.7%;
        --disc-rest: 15%;
        --disc-draw: 94%;
        --disc-sag: 1.6%;
        --group-shift: -59%;
        --spin: 14s;
        --disc-base: rgb(0 0 0 / 0.7);
        --backlight-x: 42%;
        --backlight-y: 38%;
        --backlight-size: 72%;
        --backlight-period: 7s;
        --backlight-min: 0.8;
        --backlight-max: 1;
        --backlight-mask: radial-gradient(
            ellipse var(--backlight-size) var(--backlight-size) at var(--backlight-x) var(--backlight-y),
            rgba(0, 0, 0, 1) 0%,
            rgba(0, 0, 0, 0.88) 30%,
            rgba(0, 0, 0, 0.4) 62%,
            rgba(0, 0, 0, 0) 88%);
        --label-radius: 31.5%;
        --label-mask: radial-gradient(circle closest-side,
            transparent var(--label-radius),
            rgba(0, 0, 0, 1) calc(var(--label-radius) + .5%));
        --label-only-mask: radial-gradient(circle closest-side,
            rgba(0, 0, 0, 1) var(--label-radius),
            transparent calc(var(--label-radius) + .5%));
        --breath-angle: 4.5deg;
        --breath-period: 9s;
        --open-duration: 1750ms;
        --hold-duration: 900ms;
        --sleeve-depth: -22px;
        --interior-depth: -36px;
        --ease: cubic-bezier(0.16, 1, 0.3, 1);
        --ease-draw: cubic-bezier(0.5, 0.05, 0.25, 1);

        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: var(--assembly);
        touch-action: pan-y;
        user-select: none;
    }

    @media (min-width: 1921px) {
        .disc-showcase {
            --assembly: min(500px, 54vh, 52vw);
            margin-bottom: 2rem
        }
    }

    .disc-showcase.is-locked {
        pointer-events: none;
    }

    .disc-showcase .ripples {
        position: absolute;
        bottom: -6%;
        left: 50%;
        width: calc(var(--assembly) * 1.5);
        height: calc(var(--assembly) * 0.4);
        transform: translateX(-50%);
        pointer-events: none;
    }

    .disc-showcase .ripples > div {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        border: 1px solid oklch(0.91 0.01 240 / 40%);
    }

    .disc-showcase .ripples > div:nth-child(2) {
        border-color: oklch(0.91 0.01 240 / 25%);
    }

    .disc-showcase .ripples > div:nth-child(3) {
        border-color: oklch(0.91 0.01 240 / 15%);
    }

    .disc-showcase .assembly {
        position: relative;
        width: var(--assembly);
        aspect-ratio: 1 / 1;
        perspective: 1500px;
        transform: translateX(0);
        transition: transform 1150ms var(--ease);
    }

    .disc-showcase.is-open .assembly {
        transform: translateX(var(--group-shift));
    }

    .disc-showcase .disc-carrier {
        position: absolute;
        inset: 0;
        z-index: 1;
        transform: translateX(0);
        transition: transform 850ms var(--ease) 280ms;
    }

    .disc-showcase.is-open .disc-carrier {
        transform: translateX(var(--sleeve-travel));
        transition-duration: 950ms;
        transition-delay: 0ms;
    }

    .disc-showcase .disc-holder {
        position: absolute;
        inset: 0;
        isolation: isolate;
        will-change: transform;
        transform: translate(var(--disc-rest), 0);
        transition: transform 950ms var(--ease-draw) 0ms;
    }

    .disc-showcase.is-open .disc-holder {
        transform: translate(var(--disc-draw), var(--disc-sag));
        transition-duration: 1500ms;
        transition-delay: 240ms;
    }

    .disc-showcase .disc-holder::before {
        content: "";
        position: absolute;
        inset: var(--disc-inset);
        border-radius: 50%;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
        transition: box-shadow 1150ms var(--ease);
    }

    .disc-showcase.is-open .disc-holder::before {
        box-shadow:
            0 30px 44px -14px rgba(0, 0, 0, 0.85),
            0 10px 18px -6px rgba(0, 0, 0, 0.55),
            0 1px 0 rgba(0, 0, 0, 0.6);
    }

    .disc-showcase .disc-face {
        position: absolute;
        inset: var(--disc-inset);
        border-radius: 50%;
        background: var(--disc-base);
        filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.35));
        animation: spin var(--spin) linear infinite;
    }

    .disc-showcase .disc-backlight {
        position: absolute;
        inset: var(--disc-inset);
        pointer-events: none;
        mix-blend-mode: screen;
        -webkit-mask-image: var(--backlight-mask);
        mask-image: var(--backlight-mask);
        animation: backlight-pulse var(--backlight-period) ease-in-out infinite;
    }

    .disc-showcase .disc-backlight-face {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: var(--img-disc) center / contain no-repeat;
        -webkit-mask-image: var(--label-mask);
        mask-image: var(--label-mask);
        animation: spin var(--spin) linear infinite;
    }

    .disc-showcase .disc-label {
        position: absolute;
        inset: var(--disc-inset);
        border-radius: 50%;
        pointer-events: none;
        background: var(--img-disc) center / contain no-repeat;
        -webkit-mask-image: var(--label-only-mask);
        mask-image: var(--label-only-mask);
        animation: spin var(--spin) linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    @keyframes backlight-pulse {
        0%, 100% { opacity: var(--backlight-max); }
        50% { opacity: var(--backlight-min); }
    }

    .disc-showcase .disc-gloss {
        position: absolute;
        inset: var(--disc-inset);
        border-radius: 50%;
        pointer-events: none;
        background:
            linear-gradient(116deg,
                rgba(255, 255, 255, 0) 30%,
                rgba(255, 255, 255, 0.07) 45%,
                rgba(255, 255, 255, 0.02) 53%,
                rgba(255, 255, 255, 0) 68%),
            radial-gradient(circle at 50% 50%,
                rgba(0, 0, 0, 0) 52%,
                rgba(0, 0, 0, 0.32) 92%,
                rgba(0, 0, 0, 0.55) 100%);
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.05),
            inset 0 -10px 20px -12px rgba(0, 0, 0, 0.9);
    }

    .disc-showcase .record {
        position: absolute;
        inset: 0;
        z-index: 2;
        transform-style: preserve-3d;
        transform: rotateX(var(--tilt-x, 0deg)) rotateY(var(--tilt-y, 0deg));
        transition: transform 700ms var(--ease);
    }

    .disc-showcase .record.is-tracking {
        transition: transform 90ms linear;
    }

    .disc-showcase .record.is-breathing {
        transition-duration: 0s;
    }

    .disc-showcase .layer {
        position: absolute;
        inset: 0;
    }

    .disc-showcase .layer-interior {
        transform: translateZ(var(--interior-depth));
        background: radial-gradient(circle at 50% 42%, #171412 0%, #0b0a09 62%, #050505 100%);
        box-shadow: inset 0 8px 26px rgba(0, 0, 0, 0.9);
    }

    .disc-showcase .sleeve-carrier {
        position: absolute;
        inset: 0;
        transform: translate3d(0, 0, var(--sleeve-depth));
        transition: transform 850ms var(--ease) 280ms;
    }

    .disc-showcase.is-open .sleeve-carrier {
        transform: translate3d(var(--sleeve-travel), 0, var(--sleeve-depth));
        transition-duration: 950ms;
        transition-delay: 0ms;
    }

    .disc-showcase .sleeve {
        position: absolute;
        inset: 1.5% 2%;
        overflow: hidden;
        background: #080706;
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.6);
    }

    .disc-showcase .sleeve i {
        display: block;
        width: 100%;
        height: 100%;
        background: var(--img-water) center / cover no-repeat;
        animation: drift 40s ease-in-out infinite alternate;
    }

    @keyframes drift {
        from { transform: scale(1.06) translate3d(-1%, 0.6%, 0); }
        to { transform: scale(1.1) translate3d(1.2%, -1%, 0); }
    }

    .disc-showcase .window-depth {
        position: absolute;
        left: 50%;
        top: 50%;
        width: calc(var(--hole) * 2);
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        pointer-events: none;
        transform: translate(-50%, -50%) translateZ(-2px);
        background: radial-gradient(circle, rgba(6, 8, 12, 0) 38%, rgba(4, 6, 10, 0.62) 100%);
        box-shadow:
            inset 0 9px 16px -6px rgba(0, 0, 0, 0.9),
            inset 0 -7px 14px -8px rgba(0, 0, 0, 0.7),
            inset 0 0 0 1px rgba(232, 226, 214, 0.05);
    }

    .disc-showcase .layer-jacket {
        border: 1px solid black;
        transform: translateZ(0);
        background: var(--img-jacket) center / cover no-repeat;
        box-shadow:
            0 1px 0 rgba(232, 226, 214, 0.13),
            0 34px 40px -30px rgba(0, 0, 0, 0.67),
            0 16px 22px -14px rgba(0, 0, 0, 0.53),
            0 60px 90px -58px oklch(0.75 0.08 240 / 39%);
    }

    .disc-showcase .corona {
        position: absolute;
        left: 50%;
        top: 50%;
        width: calc(var(--hole) * 2.06);
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        pointer-events: none;
        opacity: 0.28;
        transform: translate(-50%, -50%) translateZ(1px);
        box-shadow: 0 0 44px 8px rgba(232, 226, 214, 0.45);
        transition: opacity 1000ms var(--ease);
    }

    .disc-showcase.is-open .corona {
        opacity: 0.6;
    }

    @media (prefers-reduced-motion: reduce) {
        .disc-showcase *,
        .disc-showcase *::before,
        .disc-showcase *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
</style>
